import { useState, useEffect } from "react";
import { useVoice } from "@/contexts/VoiceContext";
import { Mic, Square, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import PersonalitySelector from "@/components/PersonalitySelector";

interface VoiceAssistantProps {
  selectedPersona: "travel" | "health" | "meals" | "errands" | "home" | "work" | null;
}

const VoiceAssistant = ({ selectedPersona }: VoiceAssistantProps) => {
  const { 
    isListening, 
    isProcessing, 
    isPlaying,
    startListening, 
    stopListening,
    stopPlayback,
    selectedPersonality,
    setSelectedPersonality
  } = useVoice();
  
  const [recordingTime, setRecordingTime] = useState(0);

  // Timer for recording duration
  useEffect(() => {
    let interval: number | null = null;
    
    if (isListening) {
      setRecordingTime(0);
      interval = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else if (interval) {
      window.clearInterval(interval);
    }
    
    return () => {
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [isListening]);

  const handleToggleListening = () => {
    if (isListening) {
      console.log('VoiceAssistant: Manual stop of listening requested');
      stopListening();
    } else {
      const persona = selectedPersona || "general";
      console.log(`VoiceAssistant: Manual start of listening with persona: ${persona}`);
      startListening(persona);
    }
  };

  const handleStopPlayback = () => {
    console.log('VoiceAssistant: Stop playback requested');
    stopPlayback();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="mt-10 flex flex-col items-center">
      <PersonalitySelector 
        selectedPersonality={selectedPersonality}
        onPersonalityChange={setSelectedPersonality}
      />
      
      <div className="text-xl mb-4 text-white">
        {isListening ? "I'm listening..." : 
         isProcessing ? "Processing..." : 
         isPlaying ? "Speaking..." : 
         "Press the mic button to start talking"}
      </div>
      
      {isListening && (
        <div className="mb-4 flex items-center gap-2 text-amber-600 font-medium">
          <Clock size={18} className="animate-pulse" />
          <span>{formatTime(recordingTime)}</span>
          {recordingTime >= 25 && (
            <span className="text-red-500 text-sm animate-pulse">
              (will auto-stop at 30s)
            </span>
          )}
        </div>
      )}
      
      <div className="flex flex-col items-center gap-4">
        <div className="flex gap-4">
          <Button 
            size="lg" 
            className={`rounded-full p-6 ${isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-primary'}`}
            onClick={handleToggleListening}
            disabled={isProcessing}
          >
            {isListening ? <Square size={24} /> : <Mic size={24} />}
          </Button>
          
          {isPlaying && (
            <Button
              size="lg"
              className="rounded-full p-6 bg-amber-500 hover:bg-amber-600"
              onClick={handleStopPlayback}
            >
              <Square size={24} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceAssistant;
